/**
 * Workbench inside a case: the case's wallets from the projected case graph
 * (GET /canvas/{id}/graph) and the "Promote to case" payload.
 *
 * Pure. The list is built from the graph payload the panel already holds, so
 * selecting a wallet never needs another request: a wallet already on the
 * canvas is selected; one that is not is added from the payload through the
 * Workbench's addNodesToCanvas() (gotcha 28).
 */
import type { CaseGraph, EnvNode } from '../../services/caseCanvas';
import type { CasePromoteResult } from '../../services/api';
import { bareWalletAddress } from '../../case-canvas/entityJoin';
import { detectChainCandidates } from '../../utils/chainDetect';
import { normalizeCanvasAddr } from '../../utils/canvasSync';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The backend refuses a non-UUID case or investigation id on the run stream (422). */
export function isUuid(value: string | null | undefined): value is string {
    return typeof value === 'string' && UUID_RE.test(value.trim());
}

/**
 * Case link for GET /investigation/run/stream. Only UUID-shaped ids are sent:
 * a malformed one would make the stream answer 422 and the run fall back to
 * the per-section POST flow without the case.
 */
export function streamCaseLink(
    caseId: string | null | undefined,
    investigationId: string | null | undefined,
): { caseId?: string; investigationId?: string } {
    if (!isUuid(caseId)) return {};
    return isUuid(investigationId)
        ? { caseId: caseId.trim(), investigationId: investigationId.trim() }
        : { caseId: caseId.trim() };
}

export interface CaseWalletRow {
    /** Canvas node id: the spine merge key equals the Workbench node id (R29). */
    id: string;
    address: string;
    chain: string | null;
    label: string;
    /** Spine entity type: wallet, exchange, ... */
    type: string;
    placementRole: string | null;
    semanticRole: string | null;
    /** `case` = a spine row; `graph_state` = folded in from a saved Workbench snapshot. */
    sourceLayer: string;
    riskScore: number;
    entityName: string | null;
    entityType: string | null;
}

const NON_WALLET_TYPES = new Set([
    'cluster', 'person', 'phone', 'vpa', 'email', 'handle', 'company', 'account', 'device',
    'identity_hub', 'document', 'annotation', 'bank', 'upi',
]);

const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

function walletRow(node: EnvNode): CaseWalletRow | null {
    const module = (node.module || 'crypto').toLowerCase();
    const type = (node.type || 'wallet').toLowerCase();
    if (module !== 'crypto' || NON_WALLET_TYPES.has(type)) return null;
    const address = bareWalletAddress(text(node.address) || text(node.canonical_id) || node.id || '');
    if (!address || detectChainCandidates(address).length === 0) return null;
    const attr = (node.attribution && typeof node.attribution === 'object' ? node.attribution : {}) as Record<string, unknown>;
    const raw = node as unknown as Record<string, unknown>;
    const label = text(node.label);
    return {
        id: normalizeCanvasAddr(text(node.id) && !node.id.startsWith('ent:') ? node.id : address),
        address,
        chain: text(node.chain) || text(attr.chain),
        label: label && label !== node.id && label !== address ? label : '',
        type,
        placementRole: text(attr.placement_role),
        semanticRole: text(attr.semantic_role),
        sourceLayer: text(raw.source_layer) || 'case',
        riskScore: typeof node.risk_score === 'number' && Number.isFinite(node.risk_score) ? node.risk_score : 0,
        entityName: text(attr.entity_name),
        entityType: text(attr.entity_type),
    };
}

/** The case's wallets, subjects first, one row per canvas id. */
export function caseWalletRows(graph: CaseGraph | null | undefined): CaseWalletRow[] {
    const seen = new Set<string>();
    const rows: CaseWalletRow[] = [];
    for (const node of graph?.nodes || []) {
        const row = walletRow(node);
        if (!row || seen.has(row.id)) continue;
        seen.add(row.id);
        rows.push(row);
    }
    const rank = (r: CaseWalletRow) => (r.placementRole === 'subject' ? 0 : r.sourceLayer === 'case' ? 1 : 2);
    return rows
        .map((r, i) => ({ r, i }))
        .sort((a, b) => rank(a.r) - rank(b.r) || a.i - b.i)
        .map(({ r }) => r);
}

interface CanvasElementLike {
    data?: Record<string, any>;
}

/** Id of the canvas node for this wallet, or null when it is not on the canvas. */
export function findCanvasNodeId(elements: readonly CanvasElementLike[], row: CaseWalletRow): string | null {
    const want = normalizeCanvasAddr(row.id);
    for (const el of elements) {
        const d = el?.data;
        if (!d || d.source || typeof d.id !== 'string') continue;
        if (d.id === row.id || normalizeCanvasAddr(d.id) === want) return d.id;
    }
    return null;
}

export function shortAddress(address: string): string {
    return address.length > 14 ? `${address.slice(0, 6)}...${address.slice(-4)}` : address;
}

/**
 * Canvas element for a case wallet that is not on the canvas yet, built from
 * the graph payload only. `case_context` keeps the spine facts on the node
 * (data layer, R12); the stylesheet decides presentation.
 */
export function caseWalletElement(row: CaseWalletRow, caseId: string): { data: Record<string, any> } {
    return {
        data: {
            id: row.id,
            label: row.label || row.entityName || shortAddress(row.address),
            type: 'address',
            ...(row.chain ? { chain: row.chain } : {}),
            risk_score: row.riskScore,
            ...(row.entityName ? { entity_name: row.entityName } : {}),
            ...(row.entityType ? { entity_type: row.entityType } : {}),
            _visibility: 1.0,
            case_context: {
                case_id: caseId,
                source_layer: row.sourceLayer,
                spine_type: row.type,
                ...(row.placementRole ? { placement_role: row.placementRole } : {}),
                ...(row.semanticRole ? { semantic_role: row.semanticRole } : {}),
            },
        },
    };
}

export type CaseWalletSelectPlan =
    | { kind: 'select'; nodeId: string }
    | { kind: 'add'; element: { data: Record<string, any> } };

/** What a click on a case wallet does: select the canvas node, or add it from the payload. */
export function planCaseWalletSelect(
    elements: readonly CanvasElementLike[],
    row: CaseWalletRow,
    caseId: string,
): CaseWalletSelectPlan {
    const nodeId = findCanvasNodeId(elements, row);
    return nodeId ? { kind: 'select', nodeId } : { kind: 'add', element: caseWalletElement(row, caseId) };
}

/**
 * Result keys the server's Flow IR builder reads (`forensic_flow.extract_flow`
 * via `promoter.flow_from_results`). The rest of a run (velocity, adaptive
 * transactions, counterparties) is not part of the flow and is not sent.
 */
export const PROMOTION_RESULT_KEYS = [
    'backward_trace', 'forward_trace', 'compliant_exchange',
    '_funding_sources', '_residual_returns', '_bridge_settlements', '_seizure_matrix', '_exploit_anchor',
] as const;

/** The run's results reduced to the Flow IR inputs, or null when no trace result is present. */
export function promotionResults(results: Record<string, any> | null | undefined): Record<string, any> | null {
    if (!results || typeof results !== 'object') return null;
    const out: Record<string, any> = {};
    for (const key of PROMOTION_RESULT_KEYS) {
        if (results[key] !== undefined && results[key] !== null) out[key] = results[key];
    }
    const hasTrace = ['backward_trace', 'forward_trace', 'compliant_exchange'].some((k) => out[k] && typeof out[k] === 'object');
    return hasTrace ? out : null;
}

/** Counts exactly as the server returned them. */
export function promotionSummary(result: Pick<CasePromoteResult,
    'nodes_written' | 'edges_written' | 'transfers_written' | 'skipped' | 'flow_nodes' | 'flow_edges'>): string {
    const skipped = Array.isArray(result.skipped) ? result.skipped.length : 0;
    const parts = [
        `Flow IR ${result.flow_nodes ?? 0} nodes, ${result.flow_edges ?? 0} edges`,
        `written ${result.nodes_written ?? 0} nodes, ${result.edges_written ?? 0} edges, ${result.transfers_written ?? 0} transfers`,
    ];
    if (skipped > 0) parts.push(`${skipped} refused`);
    return parts.join(' · ');
}
